import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth } from '../firebase';
import logo from '../assets/logo.png';
import $ from 'jquery';
import 'datatables.net';
import 'datatables.net-dt';
import * as XLSX from 'xlsx';
import './Schedule4Viewer.css';

const Schedule4Viewer = () => {
    const tableRef = useRef(null);
    const dataTableRef = useRef(null);
    const initRef = useRef(false);
    const schedule4DataRef = useRef([]);
    const isMounted = useRef(false);
    const [itemFilter, setItemFilter] = useState('');
    const [descriptionFilter, setDescriptionFilter] = useState('');
    const [treatmentReferenceFilter, setTreatmentReferenceFilter] = useState('');
    const [schedule4Data, setSchedule4Data] = useState([]);
    const isDrawing = useRef(false);
    const [isTableInitialized, setIsTableInitialized] = useState(false);
    const [isProDropdownOpen, setIsProDropdownOpen] = useState(false);
    const navigate = useNavigate();

    const expectedHeaders = useMemo(() => [
        'Item',
        'Treatment / Reference',
        'Stat Code',
        'UQ',
        'Description of Goods',
        'Rate #'
    ], []);

    const applyStyles = () => {
        const isDarkMode = $('body').hasClass('dark-mode');
        $('.custom-filter-input, .item-filter-select').css({
            height: '35px',
            borderRadius: '6px',
            padding: '8px 12px',
            width: '200px',
            fontSize: '14px',
            border: '1px solid ' + (isDarkMode ? '#475569 ' : '#ced4da'),
            background: isDarkMode ? '#0f172a' : '#fff',
            color: isDarkMode ? '#fff' : '#1e3a5f',
            boxSizing: 'border-box',
            lineHeight: '1.5',
            marginRight: '10px'
        });
        $('.dt-paging button.dt-paging-button').css({
            padding: '8px 12px',
            fontSize: '14px',
            fontWeight: '500',
            border: '1px solid ' + (isDarkMode ? '#475569 ' : '#ced4da'),
            borderRadius: '6px',
            backgroundColor: isDarkMode ? '#0f172a' : '#fff',
            color: isDarkMode ? '#ccc' : '#1e3a5f',
            cursor: 'pointer',
            margin: '0 4px',
            boxSizing: 'border-box',
            transition: 'background-color 0.2s, color 0.2s'
        });
        $('.dt-paging button.dt-paging-button:not(.disabled):hover').css({
            backgroundColor: isDarkMode ? '#475569 ' : '#f0f4f8',
            color: isDarkMode ? '#fff' : '#007bff'
        });
        $('.dt-paging button.dt-paging-button.current').css({
            backgroundColor: isDarkMode ? '#007bff' : '#007bff',
            color: '#fff',
            borderColor: isDarkMode ? '#007bff' : '#007bff'
        });
        $('.dt-paging button.dt-paging-button.disabled').css({
            cursor: 'not-allowed',
            opacity: '0.5'
        });
        $('#schedule4Table').addClass('table-visible');
    };

    useEffect(() => {
        if (isMounted.current) return;
        isMounted.current = true;

        const loadSchedule4Data = async () => {
            if (schedule4DataRef.current.length > 0) {
                return;
            }

            try {
                const response = await fetch('/schedule4.xlsx');
                if (!response.ok) throw new Error('Failed to fetch Schedule 4 data');
                const arrayBuffer = await response.arrayBuffer();
                const workbook = XLSX.read(arrayBuffer, { type: 'array' });
                const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
                if (!firstSheet) throw new Error('Invalid Excel file');

                let loadedSchedule4Data = XLSX.utils.sheet_to_json(firstSheet, { defval: '' });
                loadedSchedule4Data = loadedSchedule4Data.map(row => {
                    const newRow = {};
                    expectedHeaders.forEach(header => {
                        newRow[header] = row[header] !== undefined ? String(row[header]).trim() : '';
                    });
                    return newRow;
                });

                schedule4DataRef.current = loadedSchedule4Data;
                setSchedule4Data(loadedSchedule4Data);
            } catch (error) {
                console.error('Error loading Schedule 4 data:', error);
                alert('Failed to load Schedule 4 data. Please try again later.');
                schedule4DataRef.current = [];
                setSchedule4Data([]);
            }

            if (!tableRef.current) return;

            if (initRef.current && dataTableRef.current) {
                try {
                    dataTableRef.current.destroy(false);
                } catch (error) {
                    console.error('Error destroying DataTable:', error);
                }
                initRef.current = false;
            }

            applyStyles();

            if (!$.fn.DataTable.isDataTable(tableRef.current)) {
                try {
                    dataTableRef.current = $(tableRef.current).DataTable({
                        columns: expectedHeaders.map(header => ({
                            title: header,
                            data: header,
                            render: (data, type, row, meta) => {
                                return data || '';
                            }
                        })),
                        columnDefs: [
                            { width: '50px', targets: 0 },
                            { width: '110px', targets: 1 },
                            { width: '80px', targets: 2 },
                            { width: '30px', targets: 3 },
                            { width: 'auto', targets: 4 },
                            { width: '250px', targets: 5 }
                        ],
                        pageLength: 15,
                        lengthMenu: [15, 25, 50, 100],
                        searching: true,
                        ordering: true,
                        order: [], // Preserve original order from XLSX
                        responsive: true,
                        language: {
                            search: '',
                            searchPlaceholder: 'Filter records...',
                            lengthMenu: 'Show _MENU_ entries',
                        },
                        dom: '<"table-header"l>t<"table-footer"ip>',
                        deferRender: true
                    });
                    initRef.current = true;
                    dataTableRef.current.clear().rows.add(schedule4DataRef.current).draw();
                    dataTableRef.current.on('draw', () => {
                        applyStyles();
                        isDrawing.current = false;
                    });
                    setIsTableInitialized(true);
                } catch (error) {
                    console.error('Error initializing DataTable:', error);
                    alert('Failed to initialize table.');
                }
            }
        };

        loadSchedule4Data();

        return () => {
            if (dataTableRef.current && !document.getElementById('schedule4Table')) {
                try {
                    dataTableRef.current.destroy(false);
                } catch (error) {
                    console.error('Error destroying DataTable:', error);
                }
            }
            $.fn.dataTable.ext.search = [];
            isMounted.current = false;
        };
    }, [expectedHeaders]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            const dropdown = document.querySelector('.pro-dropdown');
            if (dropdown && !dropdown.contains(event.target)) {
                setIsProDropdownOpen(false);
            }
        };

        if (isProDropdownOpen) {
            document.addEventListener('click', handleClickOutside, { capture: true });
        }

        return () => {
            document.removeEventListener('click', handleClickOutside, { capture: true });
        };
    }, [isProDropdownOpen]);

    const itemOptions = useMemo(() => {
        const items = [...new Set(schedule4Data.map(row => row['Item']))].filter(item => item).sort((a, b) => parseInt(a) - parseInt(b));
        return ['', ...items];
    }, [schedule4Data]);

    useEffect(() => {
        if (!dataTableRef.current || !isTableInitialized) return;

        const timeoutId = setTimeout(() => {
            if (isDrawing.current) return;
            isDrawing.current = true;

            $.fn.dataTable.ext.search = [];

            const filterFunction = (settings, data, dataIndex, rowData, counter) => {
                if (settings.nTable.id !== 'schedule4Table') return true;
                const item = String(data[0] || '').toLowerCase();
                const treatmentReference = String(data[1] || '').toLowerCase();
                const description = String(data[4] || '').toLowerCase();
                const itemFilterValue = itemFilter.toLowerCase().trim();
                const treatmentReferenceFilterValue = treatmentReferenceFilter.toLowerCase().trim();
                const descriptionFilterValue = descriptionFilter.toLowerCase().trim();

                let itemMatch = true;
                let treatmentReferenceMatch = true;
                let descriptionMatch = true;

                if (itemFilterValue) {
                    itemMatch = item === itemFilterValue;
                }

                if (treatmentReferenceFilterValue) {
                    treatmentReferenceMatch = treatmentReference.includes(treatmentReferenceFilterValue);
                }

                if (descriptionFilterValue) {
                    descriptionMatch = description.includes(descriptionFilterValue);
                }

                return itemMatch && treatmentReferenceMatch && descriptionMatch;
            };

            $.fn.dataTable.ext.search.push(filterFunction);

            try {
                dataTableRef.current
                    .column(0)
                    .search('')
                    .column(1)
                    .search('')
                    .column(4)
                    .search('')
                    .draw('full-reset');
            } catch (error) {
                console.error('Error during DataTable filter draw:', error);
                isDrawing.current = false;
            }
        }, 400);

        return () => clearTimeout(timeoutId);
    }, [itemFilter, descriptionFilter, treatmentReferenceFilter, isTableInitialized]);

    const handleItemFilterChange = useCallback((e) => {
        setItemFilter(e.target.value);
    }, []);

    const handleDescriptionFilterChange = useCallback((e) => {
        setDescriptionFilter(e.target.value);
    }, []);

    const handleTreatmentReferenceFilterChange = useCallback((e) => {
        setTreatmentReferenceFilter(e.target.value);
    }, []);

    const handleLogout = () => {
        signOut(auth)
            .then(() => {
                navigate('/');
            })
            .catch((err) => console.error('Logout error:', err));
    };

    const resetTable = useCallback(() => {
        if (dataTableRef.current) {
            setItemFilter('');
            setTreatmentReferenceFilter('');
            setDescriptionFilter('');
            dataTableRef.current
                .column(0)
                .search('')
                .column(1)
                .search('')
                .column(4)
                .search('')
                .draw();
            dataTableRef.current.page(0).draw('page');
            applyStyles();
        }
    }, []);

    return (
        <div className="schedule4-viewer-container">
            <header className="header">
                <div className="header-content">
                    <img src={logo} alt="Customs Pro AI Logo" />
                    <h1>Customs Schedule 4</h1>
                    <nav className="nav">
                        <Link to="/" className="nav-item">Home</Link>
                        <Link to="/app" className="nav-item">Classify Goods</Link>
                        <Link to="/customs-chat" className="nav-item">Customs Chat</Link>
                        <div
                            className={`nav-item pro-dropdown ${isProDropdownOpen ? 'open' : ''}`}
                            onClick={() => setIsProDropdownOpen(!isProDropdownOpen)}
                            role="button"
                            aria-expanded={isProDropdownOpen}
                            aria-controls="pro-dropdown-menu"
                        >
                            <span className="active">Pro</span>
                            <div className="dropdown-content" id="pro-dropdown-menu" onClick={(e) => e.stopPropagation()}>
                                <Link to="/tariff-viewer" className="dropdown-item">Tariff Viewer</Link>
                                <Link to="/tco-viewer" className="dropdown-item">TCO Viewer</Link>
                                <Link to="/precedent-viewer" className="dropdown-item">Precedent Viewer</Link>
                                <Link to="/ahecc-viewer" className="dropdown-item">AHECC Viewer</Link>
                                <Link to="/schedule4-viewer" className="dropdown-item active">Schedule 4 Viewer</Link>
                                <Link to="/refund-reason-viewer" className="dropdown-item">Refund Reason Codes</Link>
                                <Link to="/gst-exemptions-viewer" className="dropdown-item">GST Exemption Codes</Link>
                                <Link to="/customs-depots-viewer" className="dropdown-item" onClick={() => setIsProDropdownOpen(false)}>Customs Depot Codes</Link>
                                <Link to="/quarantine-depots-viewer" className="dropdown-item"onClick={() => setIsProDropdownOpen(false)}>Quarantine Depot Codes</Link>
                                <Link to="/history" className="dropdown-item">History</Link>
                            </div>
                        </div>
                        <Link to="/settings" className="nav-item">Settings</Link>
                        <span className="nav-item logout" onClick={handleLogout}>Logout</span>
                    </nav>
                </div>        
            </header>
            <div className="main-content">
                <div className="table-panel">
                    <div className="table-header">
                        <div id="customFilter">
                            <select
                                value={itemFilter}
                                onChange={handleItemFilterChange}
                                className="item-filter-select"
                            >
                                <option value="">All Items</option>
                                {itemOptions.map(item => (
                                    <option key={item} value={item}>{item}</option>
                                ))}
                            </select>
                            <input
                                type="text"
                                placeholder="Filter Treatment / Reference..."
                                value={treatmentReferenceFilter}
                                onChange={handleTreatmentReferenceFilterChange}
                                className="custom-filter-input"
                            />
                            <input
                                type="text"
                                placeholder="Filter Description..."
                                value={descriptionFilter}
                                onChange={handleDescriptionFilterChange}
                                className="custom-filter-input"
                            />
                        </div>
                        <button id="resetTableBtn" onClick={resetTable}>Reset Table</button>
                    </div>
                    <table id="schedule4Table" className="display" ref={tableRef}></table>
                </div>
            </div>
        </div>
    );
};

export default Schedule4Viewer;